/**
 * Gestion du raccourci caché permettant
 * de fermer la session du visiteur.
 */
class EventHiddenLogout {

	/**
	 * Nombre de clics nécessaires pour déclencher
	 * la demande de déconnexion.
	 *
	 * @type {number}
	 */
	static REQUIRED_CLICKS = 10;

	/**
	 * Durée maximale entre deux clics.
	 *
	 * Au-delà de cette durée, le compteur repart à zéro.
	 *
	 * @type {number}
	 */
	static CLICK_TIMEOUT = 1500;

	/**
	 * Initialise le composant.
	 */
	constructor() {
		/**
		 * Élément servant de déclencheur caché.
		 *
		 * @type {HTMLElement|null}
		 */
		this.button = document.getElementById(
			'logout_hidden_button'
		);

		/**
		 * Nombre de clics consécutifs.
		 *
		 * @type {number}
		 */
		this.clickCount = 0;

		/**
		 * Timer utilisé pour réinitialiser
		 * automatiquement le compteur.
		 *
		 * @type {number|null}
		 */
		this.resetTimer = null;

		/**
		 * Arrête l'initialisation lorsque
		 * le bouton n'existe pas sur la page.
		 */
		if ( ! this.button ) {
			return;
		}

		this.bindEvents();
	}

	/**
	 * Enregistre les événements.
	 *
	 * @return {void}
	 */
	bindEvents() {
		this.button.addEventListener(
			'click',
			() => this.handleClick()
		);
	}

	/**
	 * Traite un clic sur le déclencheur secret.
	 *
	 * @return {void}
	 */
	handleClick() {
		/**
		 * Annule le timer précédent afin que
		 * les clics rapides constituent une seule série.
		 */
		if ( null !== this.resetTimer ) {
			window.clearTimeout(
				this.resetTimer
			);
		}

		/**
		 * Incrémente le compteur.
		 */
		this.clickCount += 1;

		/**
		 * Déclenche la déconnexion après
		 * dix clics consécutifs.
		 */
		if (
			this.clickCount
			>= EventHiddenLogout.REQUIRED_CLICKS
		) {
			this.clickCount = 0;

			this.confirmLogout();

			return;
		}

		/**
		 * Réinitialise le compteur lorsque l'utilisateur
		 * attend trop longtemps avant le clic suivant.
		 */
		this.resetTimer = window.setTimeout(
			() => {
				this.clickCount = 0;
				this.resetTimer = null;
			},
			EventHiddenLogout.CLICK_TIMEOUT
		);
	}

	/**
	 * Demande confirmation avant de fermer la session.
	 *
	 * @return {void}
	 */
	confirmLogout() {
		const confirmed = window.confirm(
			'Voulez-vous vraiment fermer la session ?'
		);

		if ( ! confirmed ) {
			return;
		}

		this.logout();
	}

	/**
	 * Ferme la session du visiteur.
	 *
	 * @return {Promise<void>}
	 */
	async logout() {
		/**
		 * Empêche plusieurs déconnexions simultanées.
		 */
		this.button.disabled = true;

		try {
			const response = await fetch(
				'/wp-json/event/v1/logout',
				{
					method: 'POST',
					credentials: 'same-origin',
					cache: 'no-store',
					headers: {
						'Accept': 'application/json',
					},
				}
			);

			/**
			 * Décode la réponse REST.
			 */
			const data = await response.json();

			/**
			 * Transforme une erreur HTTP
			 * en erreur JavaScript.
			 */
			if ( ! response.ok ) {
				throw new Error(
					data?.message
					|| 'Impossible de fermer la session.'
				);
			}

			/**
			 * Vérifie que la fermeture de session
			 * a bien été confirmée par le serveur.
			 */
			if ( ! data?.success ) {
				throw new Error(
					'Impossible de fermer la session.'
				);
			}

			/**
			 * Retourne vers l'écran d'identification.
			 */
			window.location.replace(
				data.redirect_to || '/'
			);
		} catch ( error ) {
			console.error(
				'[Event] Logout error:',
				error
			);

			window.alert(
				'Impossible de fermer la session.'
			);

			this.button.disabled = false;
		}
	}
}

/**
 * Initialise le raccourci de déconnexion
 * lorsque le DOM est disponible.
 */
document.addEventListener(
	'DOMContentLoaded',
	() => {
		new EventHiddenLogout();
	}
);